import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "components/ui/button";
import { Dialog, DialogContent } from "components/ui/dialog";
import { ArrowDown, ArrowUp, BookOpenTextIcon, ChevronsUpDown, Folder, Loader2, Search, X } from "lucide-react";
import { SortingState } from "@tanstack/react-table";
import { useGetFolderList } from "features/chat/hooks/use-kb";
import { useConfig } from "hooks/use-config";

const pulseAnimation = `
@keyframes brightPulse {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  50% {
    opacity: 0.7;
    box-shadow: 0 0 0 8px rgba(59, 130, 246, 0);
  }
}
`;


interface SourceDocumentSelectorProps {
    selectedFolderIds?: string[];
    onSelect?: (folderIds: string[]) => void;
}

const PAGE_SIZE = 10;

export const SourceDocumentSelector: React.FC<SourceDocumentSelectorProps> = ({
    selectedFolderIds = [],
    onSelect,
}) => {
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    const projectKey = configurationSettings?.blocksKey || '';

    const [open, setOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedFolderIds));
    const [search, setSearch] = useState("");
    const [sortingState, setSortingState] = useState<SortingState>([]);
    const [pageIndex, setPageIndex] = useState(0);

    const { data, isLoading } = useGetFolderList(projectKey, !!projectKey && open);
    const allFolders = useMemo(() => data?.data?.folders ?? [], [data]);

    useEffect(() => {
        if (open) {
            setSelectedIds(new Set(selectedFolderIds));
            setSearch("");
            setSortingState([]);
            setPageIndex(0);
        }
        // Re-sync only when the modal opens, not on every parent re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const filteredSorted = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = q
            ? allFolders.filter((f: any) => (f.Name || '').toLowerCase().includes(q))
            : allFolders.slice();

        const nameSort = sortingState.find((s) => s.id === 'name');
        const lastModifiedSort = sortingState.find((s) => s.id === 'lastModified');

        if (nameSort) {
            list.sort((a: any, b: any) => {
                const r = (a.Name || '').localeCompare(b.Name || '');
                return nameSort.desc ? -r : r;
            });
        } else if (lastModifiedSort) {
            list.sort((a: any, b: any) => {
                const at = new Date(a.LastUpdatedDate || a.CreatedDate || 0).getTime();
                const bt = new Date(b.LastUpdatedDate || b.CreatedDate || 0).getTime();
                const r = at - bt;
                return lastModifiedSort.desc ? -r : r;
            });
        }

        return list;
    }, [allFolders, search, sortingState]);

    const totalCount = filteredSorted.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const currentPage = Math.min(pageIndex, totalPages - 1);
    const pageStart = currentPage * PAGE_SIZE;
    const pageItems = filteredSorted.slice(pageStart, pageStart + PAGE_SIZE);

    const visiblePages = useMemo(() => {
        const pages: number[] = [];
        const start = Math.max(0, currentPage - 1);
        const end = Math.min(totalPages - 1, start + 2);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    }, [currentPage, totalPages]);

    const toggleSelect = (folderId: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(folderId)) {
                next.delete(folderId);
            } else {
                next.add(folderId);
            }
            return next;
        });
    };

    const goToPage = (p: number) => setPageIndex(p);
    const goPrev = () => currentPage > 0 && goToPage(currentPage - 1);
    const goNext = () => currentPage < totalPages - 1 && goToPage(currentPage + 1);

    const closeModal = () => {
        setOpen(false);
        setSearch('');
        setPageIndex(0);
    };

    const handleSave = () => {
        onSelect?.(Array.from(selectedIds));
        closeModal();
    };

    const toggleSortOrder = (columnId: 'name' | 'lastModified') => {
        setSortingState((prev) => {
            const currentSort = prev[0];
            if (!currentSort || currentSort.id !== columnId) {
                return [{ id: columnId, desc: false }];
            } else if (currentSort.desc === false) {
                return [{ id: columnId, desc: true }];
            } else {
                return [];
            }
        });
    };

    const currentNameSort = sortingState.find((s) => s.id === 'name');
    const currentLastModifiedSort = sortingState.find((s) => s.id === 'lastModified');

    const chipLabel = selectedFolderIds.length
        ? `${t("SELECT_DOCUMENT")} (${selectedFolderIds.length})`
        : t("SELECT_DOCUMENT");

    return (
        <>
            <style>{pulseAnimation}</style>
            <div
                onClick={() => setOpen(true)}
                className="relative flex items-center gap-2 h-[34px] min-w-[34px] justify-center p-2 xl:px-3 rounded-full border-[0.25px] border-black-50 dark:border-black-500 shadow-sm hover:bg-muted group cursor-pointer"
                style={selectedFolderIds.length === 0 ? {
                    animation: 'brightPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                } : undefined}
                title={chipLabel}
            >
                <div className="text-[#3D3D3D] dark:text-[#ffffff]">
                    <BookOpenTextIcon className="h-4 w-4" />
                </div>
                {selectedFolderIds.length > 0 && (
                    <span className="xl:hidden text-xs font-medium text-[#3D3D3D] dark:text-[#ffffff]">
                        {selectedFolderIds.length}
                    </span>
                )}
                <span className="text-sm font-medium hidden xl:block text-[#3D3D3D] dark:text-[#ffffff]">
                    {selectedFolderIds.length > 0 ? `${t("SELECT_DOCUMENT")} (${selectedFolderIds.length})` : t("SELECT_DOCUMENT")}
                </span>
                {selectedFolderIds.length > 0 && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIds(new Set());
                            onSelect?.([]);
                        }}
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 border border-black-50 dark:border-black-500 shadow-sm"
                        aria-label="Clear selection"
                    >
                        <X className="h-2.5 w-2.5 text-[#3D3D3D] dark:text-[#ffffff]" />
                    </button>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent 
                    className="w-[720px] max-w-[90vw] gap-0 overflow-hidden rounded-xl p-0 shadow-xl"
                    showOverlay
                    hideClose
                >
                    <div className="flex items-center justify-between px-6 py-4 border-b">
                        <h2 className="text-lg font-semibold truncate">
                            {t("SELECT_DOCUMENT")}
                        </h2>
                        <button
                            onClick={closeModal}
                            className="text-muted-foreground hover:text-foreground text-xl"
                        >
                            ×
                        </button>
                    </div>

                    <div className="flex gap-3 px-6 py-4 border-b">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
                            <input
                                value={search}
                                placeholder={t("SEARCH_DOCUMENTS")}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPageIndex(0);
                                }}
                                className="
                                    h-10 w-full rounded-3xl
                                    border border-primary
                                    bg-background pl-9 pr-8
                                    focus:outline-none
                                    focus:border-primary
                                    focus:ring-1 focus:ring-primary
                                "
                            />
                        </div>
                    </div>

                    <div className="max-h-[40vh] overflow-y-auto">
                        <div className="grid grid-cols-[1fr_auto] gap-4 items-center px-6 py-2.5 bg-muted sticky top-0 z-10">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                    {t("NAME")}
                                </span>
                                <button
                                    onClick={() => toggleSortOrder('name')}
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {currentNameSort ? (
                                        currentNameSort.desc ? (
                                            <ArrowDown className="w-4 h-4" />
                                        ) : (
                                            <ArrowUp className="w-4 h-4" />
                                        )
                                    ) : (
                                        <ChevronsUpDown className="w-4 h-4" />
                                    )}
                                </button>
                            </div>

                            <div className="flex items-center gap-2 justify-end">
                                <span className="text-sm text-muted-foreground">
                                    {t("LAST_MODIFIED")}
                                </span>
                                <button
                                    onClick={() => toggleSortOrder('lastModified')}
                                    className="text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {currentLastModifiedSort ? (
                                        currentLastModifiedSort.desc ? (
                                            <ArrowDown className="w-4 h-4" />
                                        ) : (
                                            <ArrowUp className="w-4 h-4" />
                                        )
                                    ) : (
                                        <ChevronsUpDown className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="h-auto">
                            {isLoading && (
                                <div className="flex items-center justify-center h-[40vh]">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                </div>
                            )}

                            {!isLoading && totalCount === 0 && (
                                <div className="text-sm text-muted-foreground flex items-center justify-center h-[40vh]">
                                    {t("NO_DOCUMENTS_FOUND")}
                                </div>
                            )}

                            {!isLoading && pageItems.map((folder: any) => (
                                <FolderRow
                                    key={folder._id}
                                    name={folder.Name || 'Untitled folder'}
                                    lastModified={folder.LastUpdatedDate || folder.CreatedDate}
                                    selected={selectedIds.has(folder._id)}
                                    onClick={() => toggleSelect(folder._id)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between px-6 py-4 border-t">
                        <span className="text-sm text-muted-foreground">
                            {t("SHOWING")}{" "}
                            {pageItems.length ? pageStart + 1 : 0}
                            –
                            {pageStart + pageItems.length}{" "}
                            of {totalCount}
                        </span>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={goPrev}
                                disabled={currentPage === 0}
                                className="px-3 py-1 border rounded-md text-sm disabled:opacity-50"
                            >
                                {t("PREV")}
                            </button>

                            {visiblePages.map((page) => (
                                <button
                                    key={page}
                                    onClick={() => goToPage(page)}
                                    className={`px-3 py-1 border rounded-md text-sm ${page === currentPage ? "bg-muted font-medium" : ""
                                        }`}
                                >
                                    {page + 1}
                                </button>
                            ))}

                            <button
                                onClick={goNext}
                                disabled={currentPage >= totalPages - 1}
                                className="px-3 py-1 border rounded-md text-sm disabled:opacity-50"
                            >
                                {t("NEXT")}
                            </button>
                        </div>
                    </div>

                    <div className="flex gap-2 justify-end my-4 px-6">
                        <Button
                            variant="outline"
                            onClick={closeModal}
                            className="px-6 py-3 text-sm font-medium border rounded-lg"
                        >
                            {t("CANCEL")}
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={selectedIds.size === 0}
                            className="px-6 py-3 text-sm font-medium bg-primary text-primary-foreground rounded-lg"
                        >
                            {selectedIds.size ? `${t("DONE")} (${selectedIds.size})` : t("DONE")}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

const FolderRow = ({
    name,
    lastModified,
    selected,
    onClick,
}: {
    name: string;
    lastModified?: string | Date;
    selected?: boolean;
    onClick?: () => void;
}) => {
    const formatDate = (date: string | Date | undefined) => {
        if (!date) return '-';
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();
        return `${day}.${month}.${year}`;
    };

    return (
        <div
            onClick={onClick}
            className={`grid grid-cols-[1fr_auto] gap-4 items-center px-6 py-3 border-b cursor-pointer ${selected ? "bg-sidebar-accent" : "hover:bg-muted"
                }`}
        >
            <div className="flex items-center gap-2 min-w-0">
                <div className="h-8 w-8 flex-shrink-0 rounded-lg flex items-center justify-center bg-primary/10">
                    <Folder className="w-4 h-4 text-primary" />
                </div>
                <span className="text-sm truncate">{name}</span>
                {selected && (
                    <span className="text-primary text-xs">✓</span>
                )}
            </div>
            <div className="flex items-center justify-end">
                <span className="text-sm">{formatDate(lastModified)}</span>
            </div>
        </div>
    );
};
